import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import TurnstileWidget from '../components/TurnstileWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    company_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show Turnstile if not already shown
    if (!showTurnstile) {
      setShowTurnstile(true);
      return;
    }
    
    // Use the token from state (set by Turnstile onVerify callback)
    const token = turnstileToken;
    
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = {
        ...formData,
        turnstile_token: token || null
      };
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      
      toast.success(isLogin ? 'Erfolgreich angemeldet!' : 'Registrierung erfolgreich!');
      
      // Push sign_up event to dataLayer for GTM tracking (only on registration)
      if (!isLogin) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'sign_up'
        });
      }
      
      // Push dashboard_login event to dataLayer for GTM tracking (only on login)
      if (isLogin) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'dashboard_login'
        });
      }
      
      // Redirect admin users to admin panel
      if (response.data.user.is_admin) {
        navigate('/admin');
      } else {
        // For regular users, check if they have campaigns
        try {
          const campaignsResponse = await axios.get(`${API}/campaigns/my-campaigns`, {
            headers: { Authorization: `Bearer ${response.data.token}` }
          });
          
          const hasCampaigns = campaignsResponse.data && campaignsResponse.data.length > 0;
          
          // If registering or no campaigns, go to onboarding
          // If logging in with existing campaigns, go to dashboard
          if (!isLogin || !hasCampaigns) {
            navigate('/onboarding');
          } else {
            navigate('/dashboard');
          }
        } catch (err) {
          // If error checking campaigns, default to onboarding for safety
          navigate('/onboarding');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ein Fehler ist aufgetreten');
      // Reset Turnstile on error
      turnstileRef.current?.reset();
      setShowTurnstile(false);
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleModeToggle = () => {
    setIsLogin(!isLogin);
    setShowTurnstile(false);
    setTurnstileToken(null);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Logo */}
        <div className="p-8">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="AdSimplify Logo" className="h-8 w-8" />
            <h1 className="text-2xl font-bold text-[#0040FF]">AdSimplify</h1>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center px-8 pb-16">
          <div className="w-full max-w-md" data-testid="login-form-container">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {isLogin ? 'Anmelden' : 'Registrieren'}
              </h2>
              <p className="text-gray-600">
                {isLogin 
                  ? 'Willkommen zurück! Melde dich an, um fortzufahren.' 
                  : 'Erstelle dein Konto und starte deine erste Kampagne'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && (
                <>
                  {/* Vorname und Nachname nebeneinander */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Vorname *</Label>
                      <Input
                        id="first_name"
                        data-testid="input-first-name"
                        value={formData.first_name}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only letters, spaces, and hyphens
                          if (/^[a-zA-ZäöüÄÖÜß\s-]*$/.test(value)) {
                            setFormData({ ...formData, first_name: value });
                          }
                        }}
                        required={!isLogin}
                        placeholder="Max"
                        className="h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last_name">Nachname *</Label>
                      <Input
                        id="last_name"
                        data-testid="input-last-name"
                        value={formData.last_name}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow only letters, spaces, and hyphens
                          if (/^[a-zA-ZäöüÄÖÜß\s-]*$/.test(value)) {
                            setFormData({ ...formData, last_name: value });
                          }
                        }}
                        required={!isLogin}
                        placeholder="Mustermann"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_name">Firmenname *</Label>
                    <Input
                      id="company_name"
                      data-testid="input-company-name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required={!isLogin}
                      placeholder="z.B. Müller GmbH"
                      className="h-12"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="deine@email.com"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Passwort *</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="input-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  placeholder="••••••••"
                  className="h-12"
                />
                {!isLogin && (
                  <p className="text-xs text-gray-500">Mindestens 6 Zeichen</p>
                )}
              </div>

              {/* Turnstile Widget - Shown inline after first submit */}
              {showTurnstile && (
                <div className="flex justify-center py-2">
                  <TurnstileWidget
                    ref={turnstileRef}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => toast.error('Sicherheitsüberprüfung fehlgeschlagen')}
                    mode="strict"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#0040FF] hover:bg-[#0033CC] text-white text-base font-semibold"
                disabled={loading || (showTurnstile && !turnstileToken)}
                data-testid="submit-auth-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Bitte warten...
                  </span>
                ) : (
                  isLogin ? 'Anmelden' : 'Jetzt registrieren'
                )}
              </Button>
            </form>

            {!isLogin && (
              <div className="mt-4 text-center text-xs text-gray-600">
                <p>
                  Durch das Erstellen eines Kontos stimmen Sie unseren{' '}
                  <a href="/agb" className="text-[#0040FF] hover:text-[#0033CC] font-medium">
                    AGBs
                  </a>{' '}
                  zu und bestätigen, unsere{' '}
                  <a href="/datenschutz" className="text-[#0040FF] hover:text-[#0033CC] font-medium">
                    Datenschutzhinweise
                  </a>{' '}
                  gelesen zu haben.
                </p>
              </div>
            )}

            {isLogin && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-[#0040FF] hover:text-[#0033CC] font-medium"
                >
                  Passwort vergessen?
                </button>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200">
              {isLogin ? (
                <>
                  <p className="text-center text-sm text-gray-600 mb-3">
                    Noch kein Konto?
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleModeToggle}
                    className="w-full h-12 border-2 hover:bg-gray-50"
                    data-testid="toggle-auth-mode-btn"
                  >
                    Jetzt registrieren
                  </Button>
                </>
              ) : (
                <p className="text-center text-sm text-gray-600">
                  Du hast bereits einen Account?{' '}
                  <button
                    type="button"
                    onClick={handleModeToggle}
                    className="text-[#0040FF] hover:text-[#0033CC] font-medium hover:underline"
                    data-testid="toggle-auth-mode-btn"
                  >
                    Login
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Testimonial (Hidden on Mobile) */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0040FF] items-center justify-center p-12">
        <div className="max-w-xl text-white">
          <Quote className="w-12 h-12 mb-6 text-white/80" />
          <blockquote className="space-y-6">
            <p className="text-lg leading-relaxed">
              "Wir wollten eine einfache Lösung, die uns zuverlässig neue Umzugsanfragen bringt – ohne selbst stundenlang Kampagnen pflegen zu müssen.
            </p>
            <p className="text-lg leading-relaxed">
              Mit AdSimplify läuft das endlich rund. Die Anfragen kommen kontinuierlich rein, wir haben weniger Stress im Alltag und können uns wieder auf die Arbeit konzentrieren, statt auf Einstellungen und Optimierungen.
            </p>
            <p className="text-lg leading-relaxed">
              Für uns fühlt es sich an, als hätte jemand im Hintergrund immer ein Auge auf unsere Werbung."
            </p>
            <footer className="flex items-center gap-4 pt-4">
              <Avatar className="w-12 h-12 border-2 border-white/20">
                <AvatarFallback className="bg-white/10 text-white text-lg font-semibold">
                  YE
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">Yüksek E.</p>
                <p className="text-sm text-white/80">Geschäftsführer von einfachumzug.at</p>
              </div>
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Login;
